import type {
  DDLogEvent,
  GeolocationInvalidatedEvent,
  GeolocationPassed,
  GeolocationRejected,
  LocationFailure,
} from '@fanduel/geolocation-sdk';
import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
  geolocationFailure,
  geolocationInvalidated,
  geolocationJwtSelector,
  geolocationPassed,
  geolocationProgressSelector,
  geolocationRejected,
  startGeolocation,
} from '@/features/geolocation/store/geolocation';
import {
  GeolocationProgress,
  type SdkLogEvent,
} from '@/features/geolocation/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';
import { useAppDispatch } from '@/store/hooks';

export const useGeolocationHandlers = () => {
  const dispatch = useAppDispatch();
  const geoToken = useSelector(geolocationJwtSelector);
  const geolocationProgress = useSelector(geolocationProgressSelector);
  const sdkLogBuffer = useRef<SdkLogEvent[]>([]);

  const handleGeolocationPassed = useCallback(
    (action: GeolocationPassed) => {
      // Skip duplicate tokens only when geo already succeeded (avoids iOS game reload).
      // After a failure/rejection, the same JWT must still flip progress back to Success.
      const shouldUpdateGeolocationState =
        geoToken !== action.payload.jwt ||
        geolocationProgress !== GeolocationProgress.Success;

      if (shouldUpdateGeolocationState) {
        dispatch(geolocationPassed(action.payload.jwt));
      }

      Logger.logRumAction({
        name: 'Geolocation success',
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Success,
        service: 'geolocation-web',
      });

      const sdkEvents = sdkLogBuffer.current;
      sdkLogBuffer.current = [];

      WrapperProxy.setGeolocationSuccess({
        ...action,
        payload: { ...action.payload, sdkEvents },
      } as GeolocationPassed);
    },
    [dispatch, geoToken, geolocationProgress],
  );

  const handleGeolocationRejected = useCallback(
    (action: GeolocationRejected) => {
      dispatch(geolocationRejected(action.payload.rejection));

      Logger.logRumError({
        name: 'Geolocation rejected',
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Error,
        service: 'geolocation-web',
        context: {
          rejection: action.payload.rejection,
        },
      });

      const sdkEvents = sdkLogBuffer.current;
      sdkLogBuffer.current = [];

      WrapperProxy.setGeolocationRejected({
        ...action,
        payload: { ...action.payload, sdkEvents },
      } as GeolocationRejected);
    },
    [dispatch],
  );

  const handleLocationFailure = useCallback(
    (action: LocationFailure) => {
      dispatch(geolocationFailure(action.payload.error));
      Logger.logRumError({
        name: 'Geolocation failure',
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Error,
        service: 'geolocation-web',
        context: {
          rejection: action.payload.error,
        },
      });

      const sdkEvents = sdkLogBuffer.current;
      sdkLogBuffer.current = [];

      WrapperProxy.setGeolocationFailure({
        ...action,
        payload: { ...action.payload, sdkEvents },
      } as LocationFailure);
    },
    [dispatch],
  );

  const handleLocationInProgress = useCallback(() => {
    dispatch(startGeolocation());
    Logger.logRumAction({
      name: 'Geolocation in progress',
      useCase: DdEventCategory.Geolocation,
      eventType: DdEventType.Info,
      service: 'geolocation-web',
    });

    WrapperProxy.setGeolocationProgress();
  }, [dispatch]);

  const handleGeolocationTokenInvalidated = useCallback(
    (action: GeolocationInvalidatedEvent) => {
      dispatch(geolocationInvalidated());
      WrapperProxy.setGeolocationInvalidated(action);
    },
    [dispatch],
  );

  const handleAndroidGeolocationTokenUpdate = useCallback(
    (payload: { geocomplyToken?: string }) => {
      dispatch(geolocationPassed(payload?.geocomplyToken ?? ''));

      Logger.logRumAction({
        name: 'Geolocation ANDROID token updated',
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Info,
        service: 'geolocation-web',
      });
    },
    [dispatch],
  );

  const handleGeolocationLogEvent = useCallback((event: DDLogEvent) => {
    const { payload } = event;
    let attributes: Record<string, unknown> = {};
    try {
      if (typeof payload.attributes === 'string') {
        attributes = JSON.parse(payload.attributes);
      }
    } catch (e) {
      console.error('Failed to parse geolocation log event attributes', e);
    }
    Logger.logRumAction({
      name: payload.eventName,
      useCase: DdEventCategory.Geolocation,
      eventType: payload.logLevel.toUpperCase() as DdEventType,
      service: 'geolocation-web',
      context: attributes,
    });
    if (payload.eventName === 'SDK requested geolocation from GeoComply') {
      sdkLogBuffer.current.push({
        eventName: payload.eventName,
        logLevel: payload.logLevel,
        attributes,
      });
    }
  }, []);

  return {
    handleGeolocationPassed,
    handleGeolocationRejected,
    handleLocationFailure,
    handleLocationInProgress,
    handleAndroidGeolocationTokenUpdate,
    handleGeolocationTokenInvalidated,
    handleGeolocationLogEvent,
  };
};
